import type { ScheduleEntry } from '@myapp/contract';

/**
 * What clicking a block should open. `null` means the block is inert.
 *
 * Every id here is an **owner** id, never `entry.id`. `entry.id` is the schedule-entry row — a
 * different number for the same appointment, and `null` for a projected break. The upsert modals
 * feed their param straight into `useAppointmentDetail` / `useTaskDetail`, and the Zeitmanagement
 * edit route calls `useAbsenceDetail` on its `:absenceId` param, so all three want the owner.
 */
export type BlockTarget =
  | { kind: 'appointment'; id: number }
  | { kind: 'task'; id: number }
  | { kind: 'absence'; id: number }
  | null;

/**
 * The single place that decides whether a block is clickable.
 *
 * Breaks have no owner to edit, and any entry whose owner id is missing is treated the same way —
 * better inert than opening a modal on `undefined`.
 */
export function resolveBlockTarget(entry: ScheduleEntry): BlockTarget {
  switch (entry.type) {
    case 'APPOINTMENT':
      return entry.appointmentId != null
        ? { kind: 'appointment', id: entry.appointmentId }
        : null;
    case 'TASK':
      return entry.taskId != null ? { kind: 'task', id: entry.taskId } : null;
    case 'ABSENCE':
      return entry.absenceId != null ? { kind: 'absence', id: entry.absenceId } : null;
    case 'BREAK':
    default:
      return null;
  }
}
